/**
 * How an MOU's document HTML (Quill output, sanitised server-side) reads on
 * screen — the signing page and the detail preview share it, so the client and
 * the sender see the same layout.
 *
 * Quill 2 writes bullet lists as <ol><li data-list="bullet">, hence the
 * attribute variant: without it every bullet would be numbered.
 */
export const DOC_CLASSES = [
  "text-sm leading-relaxed text-slate-700 dark:text-slate-300",
  "[&_h1]:mb-2 [&_h1]:mt-4 [&_h1]:text-lg [&_h1]:font-semibold [&_h1]:text-slate-900 dark:[&_h1]:text-white",
  "[&_h2]:mb-2 [&_h2]:mt-4 [&_h2]:text-base [&_h2]:font-semibold [&_h2]:text-slate-900 dark:[&_h2]:text-white",
  "[&_h3]:mb-1 [&_h3]:mt-3 [&_h3]:text-sm [&_h3]:font-semibold [&_h3]:text-slate-900 dark:[&_h3]:text-white",
  "[&_p]:mb-2",
  "[&_ul]:mb-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:mb-2 [&_ol]:list-decimal [&_ol]:pl-5",
  "[&_li[data-list=bullet]]:list-disc [&_li]:mb-0.5",
  "[&_li.ql-indent-1]:ml-6 [&_li.ql-indent-2]:ml-12 [&_li.ql-indent-3]:ml-[4.5rem]",
  "[&_blockquote]:border-l-2 [&_blockquote]:border-slate-300 [&_blockquote]:pl-3 [&_blockquote]:italic",
  "[&_a]:text-indigo-600 [&_a]:underline dark:[&_a]:text-indigo-400",
  "[&_.ql-align-center]:text-center [&_.ql-align-right]:text-right [&_.ql-align-justify]:text-justify",
  "[&_table]:mb-3 [&_table]:w-full [&_table]:border-collapse",
  "[&_td]:border [&_td]:border-slate-300 [&_td]:px-2 [&_td]:py-1 [&_td]:align-top dark:[&_td]:border-slate-600",
  "[&_th]:border [&_th]:border-slate-300 [&_th]:bg-slate-50 [&_th]:px-2 [&_th]:py-1 dark:[&_th]:border-slate-600 dark:[&_th]:bg-slate-800",
  "[&_.mou-pb]:my-3 [&_.mou-pb]:border-t-2 [&_.mou-pb]:border-dashed [&_.mou-pb]:border-slate-300 [&_.mou-pb]:pt-1 [&_.mou-pb]:text-center [&_.mou-pb]:text-[10px] [&_.mou-pb]:uppercase [&_.mou-pb]:tracking-wide [&_.mou-pb]:text-slate-400",
  "[&_.mou-sig]:my-3 [&_.mou-sig]:rounded-md [&_.mou-sig]:border [&_.mou-sig]:border-dashed [&_.mou-sig]:border-slate-300 [&_.mou-sig]:px-3 [&_.mou-sig]:py-2 [&_.mou-sig]:text-xs [&_.mou-sig]:text-slate-500 dark:[&_.mou-sig]:border-slate-600",
].join(" ");

const layoutRe = (key) =>
  new RegExp(`<p(?:\\s[^>]*)?>\\s*\\{\\{\\s*${key}\\s*\\}\\}\\s*</p>|\\{\\{\\s*${key}\\s*\\}\\}`, "g");

/**
 * The body with {{page_break}} / {{signature_block}} shown as what they do,
 * instead of as raw placeholders. Screen only — the PDF builder replaces them
 * with a real page break and a real signature table.
 */
export function withLayoutMarkers(html) {
  return (html || "")
    .replace(layoutRe("page_break"), '<div class="mou-pb">New page</div>')
    .replace(layoutRe("signature_block"),
             '<div class="mou-sig">✍ Signatures of both parties are printed here in the PDF</div>');
}
